import EmblaCarousel, { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'

interface SlideshowComponent {
	embla: EmblaCarouselType | null
	init(wrapper: HTMLElement, options: EmblaOptionsType): void
	addTogglePrevNextBtnsActive(prevBtn: HTMLElement, nextBtn: HTMLElement): () => void
	addPrevNextBtnsClickHandlers(prevBtn: HTMLElement, nextBtn: HTMLElement): () => void
	addDotBtnsAndClickHandlers(dots: HTMLElement): () => void
	scrollTo(index: number): void
	onSlideChange(callback: (index: number) => void): void
}

export default {
	name: 'slider',
	component(): SlideshowComponent {
		return {
			embla: null,

			init(wrapper: HTMLElement, options: EmblaOptionsType) {
				if (!wrapper) return

				const emblaNode = wrapper.querySelector<HTMLElement>('.embla__viewport')

				if (!emblaNode) return

				this.embla = EmblaCarousel(emblaNode, options, [
					WheelGesturesPlugin(),
					Autoplay({
						playOnInit: true,
						delay: 3000
					})
				])

				const prevBtnNode = wrapper.querySelector<HTMLElement>('.embla__button--prev')
				const nextBtnNode = wrapper.querySelector<HTMLElement>('.embla__button--next')

				if (prevBtnNode && nextBtnNode) {
					const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(prevBtnNode, nextBtnNode)
					this.embla?.on('destroy', removePrevNextBtnsClickHandlers)
				}

				const dots = wrapper.querySelector<HTMLElement>('.embla__dots')
				if (dots) {
					const removeDotBtnsAndClickHandlers = this.addDotBtnsAndClickHandlers(dots)
					this.embla?.on('destroy', removeDotBtnsAndClickHandlers)
				}
			},

			onSlideChange(callback: (index: number) => void) {
				this.embla?.on('select', () => {
					callback(this.embla?.selectedScrollSnap() ?? 0)
				})
			},

			addTogglePrevNextBtnsActive(prevBtn, nextBtn) {
				const togglePrevNextBtnsState = () => {
					prevBtn.toggleAttribute('disabled', !this.embla?.canScrollPrev())
					nextBtn.toggleAttribute('disabled', !this.embla?.canScrollNext())
				}

				this.embla?.on('select', togglePrevNextBtnsState).on('init', togglePrevNextBtnsState).on('reInit', togglePrevNextBtnsState)

				return () => {
					prevBtn.removeAttribute('disabled')
					nextBtn.removeAttribute('disabled')
				}
			},

			addPrevNextBtnsClickHandlers(prevBtn, nextBtn) {
				const scrollPrev = () => this.embla?.scrollPrev()
				const scrollNext = () => this.embla?.scrollNext()

				prevBtn.addEventListener('click', scrollPrev)
				nextBtn.addEventListener('click', scrollNext)

				const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(prevBtn, nextBtn)

				return () => {
					removeTogglePrevNextBtnsActive()
					prevBtn.removeEventListener('click', scrollPrev)
					nextBtn.removeEventListener('click', scrollNext)
				}
			},

			addDotBtnsAndClickHandlers(dots) {
				let dotNodes: HTMLElement[] = []

				const addDotBtnsWithClickHandlers = () => {
					if (!this.embla) return
					const snapPoints = this.embla.scrollSnapList()
					// Responsive: hide dots when only one snap for current viewport
					if (snapPoints.length <= 1) {
						dots.innerHTML = ''
						dots.style.display = 'none'
						dotNodes = []
						return
					}

					// Ensure visible when multiple snaps
					dots.style.display = ''
					dots.innerHTML = snapPoints.map((_, index) => `<button aria-label="Slide ${index + 1}" class="embla__dot" type="button"></button>`).join('')

					dotNodes = Array.from(dots.querySelectorAll<HTMLElement>('.embla__dot'))
					dotNodes.forEach((dotNode, index) => {
						dotNode.addEventListener('click', () => this.embla?.scrollTo(index))
					})
				}

				const toggleDotBtnsActive = () => {
					if (!dotNodes.length) return
					const previous = this.embla?.previousScrollSnap()
					const selected = this.embla?.selectedScrollSnap()
					dotNodes[previous ?? 0]?.classList.remove('embla__dot--selected')
					dotNodes[selected ?? 0]?.classList.add('embla__dot--selected')
				}

				this.embla
					?.on('init', addDotBtnsWithClickHandlers)
					.on('reInit', addDotBtnsWithClickHandlers)
					.on('init', toggleDotBtnsActive)
					.on('reInit', toggleDotBtnsActive)
					.on('select', toggleDotBtnsActive)

				return () => {
					dots.innerHTML = ''
				}
			},

			scrollTo(index: number) {
				this.embla?.scrollTo(index)
			}
		}
	}
}
